import { Component, OnInit, Input, OnDestroy, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService, ConfirmationService } from 'primeng/api';

import { DropdownModule } from 'primeng/dropdown';
import { PriceVariable } from 'src/app/shared/price-variable';
import { ProductApiService } from 'src/app/services/product-api.service';


/**
 * Reusable price-variable dropdown selector backed by `ProductApiService`.
 *
 * On init the component loads the full `PriceVariable` list, appending a computed
 * `VARCODESTR = VARCODE + ' (' + VARVALUE + ')'` display string to each entry.  The initial
 * selection is resolved from `selectedID` after load.
 *
 * On `selectedID` input change the component re-resolves `_selectedVariable` from the
 * already-loaded list without a new HTTP call.
 *
 * Action buttons (`onAddButton`/`onEditButton`) open a small dialog for the 3 editable
 * fields (`VARCODE`/`VARVALUE`/`VARNOTE`) and call `postPriceVariableById`/
 * `putPriceVariableById` on confirm. `onDeleteButton` confirms via `ConfirmationService`
 * first, then calls `deletePriceVariableById`; a `409` response (variable still referenced
 * by a vendor price formula) surfaces its message via toast rather than a generic error —
 * see `deletePriceVariableById`'s own doc comment in `product-api.service.ts`.
 *
 * `VARID` is not identity in `[dbo].[Variables]` (confirmed via the backend's own SQL
 * template — see `routes/Variables.ts`), so a new row's `VARID` is computed client-side as
 * `max(existing VARID) + 1` from the already-loaded `variablesList`, mirroring the MAX+1
 * convention this codebase's backend already uses for other non-identity keys
 * (`CurrencyRates.ts`, `product/vendors.ts`). Not user-editable.
 *
 * **Output:** `selectedVariable` emits the resolved `PriceVariable` on click and on change.
 *
 * **Note:** a blank `@Input()` decorator appears before the `selectedID` accessor — this is a
 * pre-existing code artefact and is preserved as-is.
 */
@Component({
  selector: 'app-variable-selector',
  templateUrl: './variable-selector.component.html',
  styleUrls: ['./variable-selector.component.css']
})
export class VariableSelectorComponent implements OnInit, OnChanges, OnDestroy {

  /** Full list of price variables loaded from the API, with `VARCODESTR` appended. */
  variablesList: PriceVariable[];
  /** Currently resolved `PriceVariable` object matching `_selectedID`. */
  _selectedVariable: PriceVariable;

  private _priceVarQuery: Subscription;
  private _writeQuery: Subscription;

  _loading: boolean;
  totalRecordsMC: number;

  selectedCompany: string;

  /** `true` while the Add/Edit dialog is open. */
  _dialogVisible: boolean = false;
  /** `'add'` when the dialog is creating a new variable, `'edit'` when updating `_selectedVariable`. */
  _dialogMode: 'add' | 'edit' = 'add';
  /** Form model bound to the dialog's 3 fields. */
  _dialogModel: PriceVariable = null;
  /** `true` while a save/delete request is in flight — disables dialog/action buttons. */
  _saving: boolean = false;

  @ViewChild('VariableSelectorDd') CompanySelector: DropdownModule;

  /** Label text displayed above the dropdown. */
  @Input() labelKey = 'Переменная';
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Выбрать...';

  // Pre-existing blank @Input() decorator — preserved as-is.
  @Input()

  /** The ID of the selected variable (`PriceVariable.VARID`). Triggers re-resolution on change. */
  @Input()
  get selectedID(): number { return this._selectedID; }
  set selectedID(id: number) {
    this._selectedID = id;
  }
  private _selectedID: number = null;

  /** `true` when a variable is currently resolved (`_selectedVariable` is non-null). */
  public get isThereSelection(): boolean { return (this._selectedVariable ? true : false) };

  /** When `true`, the dropdown is rendered as read-only. */
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) {
    this._disabled = b;
  }
  _disabled: boolean = false;

  /** When `true`, the dropdown is rendered as read-only (CSS-level). */
  @Input() readonly: boolean = false;
  /** When `false`, the label above the dropdown is hidden. */
  @Input() showLabel: boolean = true;
  /** When `true`, the Edit button is rendered. */
  @Input() showEditButton: boolean = true;
  /** When `true`, the Add button is rendered. */
  @Input() showAddButton: boolean = true;
  /** When `true`, the Delete button is rendered. */
  @Input() showDeleteButton: boolean = true;

  public get doShowLabel(): boolean { return this.showLabel; };
  public get doShowEditButton(): boolean { return this.showEditButton; };
  public get doShowAddButton(): boolean { return this.showAddButton; };
  public get doShowDeleteButton(): boolean { return this.showDeleteButton; };

  /** Emits the selected `PriceVariable` on click and on change. */
  @Output()
  public selectedVariable: EventEmitter<PriceVariable> = new EventEmitter();

  /*
   * TODO:
   * Add the messanger service to inform about company and subsidiary choice
   */
  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) { }

  /** Resolves `_selectedVariable` from `variablesList` based on `_selectedID`. */
  private _selectVariable(): void {
    console.log('*** VariableSelectorComponent onCategoryEditButton: ');
    this._selectedVariable = (this._selectedID && this.variablesList) ? this.variablesList.find(x => x.VARID == this._selectedID) : null;
  }

  /** Loads the full `PriceVariable` list, appends `VARCODESTR` display strings, then re-resolves the selection. */
  private _reloadComponentData(): void {
    console.log('*** VariableSelectorComponent onCategoryEditButton: ');

    this._loading = true;

    this._priceVarQuery =
      (this.prodApi.getPriceVariables())
      .subscribe({
        next: (data: PriceVariable[]) => {
          this.variablesList = data;
          this.totalRecordsMC = data.length;
          this.variablesList.forEach(e => e.VARCODESTR = e.VARCODE + ' (' + e.VARVALUE + ')');
          this._loading = false;

          console.log('*** VariableSelectorComponent ngOnInit (totalRecordsMC): ' + data.length);

          this._selectVariable();
        },
        error: (err) => {
          console.error('*** VariableSelectorComponent _reloadComponentData: Error: ' + err);
          this._loading = false;
          this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося завантажити список змінних' });
        }
      });
  }

  /** Reacts to `selectedID` changes by re-resolving `_selectedVariable` from the cached list. */
  ngOnChanges(changes: SimpleChanges) {
    console.log('*** VariableSelectorComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      //console.log('*** VariableSelectorComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** VariableSelectorComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'selectedID':
          this._selectVariable();
          break;
      }

    }
  }

  /** Unsubscribes the price-variable HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** VariableSelectorComponent ngOnDestroy:');
    if (this._priceVarQuery) { this._priceVarQuery.unsubscribe(); }
    if (this._writeQuery) { this._writeQuery.unsubscribe(); }
  }

  /** Triggers the initial price-variable list load. */
  ngOnInit(): void {
    console.log('*** VariableSelectorComponent ngOnInit:');
    this._reloadComponentData();
  }

  /** Handles dropdown `(onClick)` — emits the current `_selectedVariable`. */
  onClickVariable(e) {
    console.log('*** VariableSelectorComponent onClickVariable: ' + e)

    this.selectedVariable.emit(this._selectedVariable);
  }

  /** Handles dropdown `(onChange)` — syncs `_selectedVariable` and `_selectedID` from the event, then emits. */
  onChangeVariable(e) {
    console.log('*** VariableSelectorComponent onChangeVariable: ' + e)

    this._selectedVariable = e.value;

    //console.log('*** globalSettingsDialog onChangeMainCompany: selectedCompany: ' + this._clientMC.CLIENTSNIKNAME)

    this._selectedID = this._selectedVariable ? this._selectedVariable.VARID : null;

    this.selectedVariable.emit(this._selectedVariable);

  }

  /** Add-variable button handler — opens the dialog with a blank form. */
  onAddButton(e) {
    console.log('*** VariableSelectorComponent onCategoryAddButton: ' + JSON.stringify(e));

    this._dialogMode = 'add';
    this._dialogModel = { VARID: 0, VARCODE: '', VARVALUE: '', VARNOTE: '' };
    this._dialogVisible = true;
  }

  /** Edit-variable button handler — opens the dialog pre-filled from `_selectedVariable`. */
  onEditButton(e) {
    console.log('*** VariableSelectorComponent onCategoryEditButton: ' + JSON.stringify(e));
    console.log('*** VariableSelectorComponent onCategoryEditButton: about to edit company ');

    if (!this._selectedVariable) { return; }

    this._dialogMode = 'edit';
    // Clone — don't mutate the list's own object until the save actually succeeds.
    this._dialogModel = { ...this._selectedVariable };
    this._dialogVisible = true;
  }

  /** Delete-variable button handler — confirms first, then deletes; surfaces a 409 "still referenced" message via toast. */
  onDeleteButton(e) {
    console.log('*** VariableSelectorComponent onCategoryEditButton: ' + JSON.stringify(e));
    console.log('*** VariableSelectorComponent onCategoryEditButton: about to edit company ');

    if (!this._selectedVariable) { return; }

    const target = this._selectedVariable;

    this.confirmationService.confirm({
      message: `Видалити змінну "${target.VARCODE}"?`,
      header: 'Підтвердження видалення',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Так',
      rejectLabel: 'Ні',
      accept: () => {
        this._saving = true;
        this._writeQuery = this.prodApi.deletePriceVariableById(target.VARID)
          .subscribe({
            next: () => {
              this._saving = false;
              this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Змінну видалено' });
              this._selectedVariable = null;
              this._selectedID = null;
              this._reloadComponentData();
            },
            error: (err) => {
              this._saving = false;
              console.error('*** VariableSelectorComponent onDeleteButton: Error: ' + JSON.stringify(err));
              const detail = err?.error?.error || err?.error?.message || 'Не вдалося видалити змінну';
              this.messageService.add({ severity: 'error', summary: 'Помилка видалення', detail, life: 8000 });
            }
          });
      }
    });
  }

  /** Confirms the Add/Edit dialog — creates or updates the variable, then reloads and closes on success. */
  onDialogSave(): void {
    if (!this._dialogModel || !this._dialogModel.VARCODE) { return; }

    this._saving = true;

    // VARCODESTR is a display-only computed field, never sent back to the API.
    const { VARCODESTR, ...pv } = this._dialogModel;

    if (this._dialogMode === 'add') {
      const nextId = (this.variablesList && this.variablesList.length)
        ? Math.max(...this.variablesList.map(v => v.VARID)) + 1
        : 1;
      pv.VARID = nextId;

      this._writeQuery = this.prodApi.postPriceVariableById(nextId, pv)
        .subscribe({
          next: () => {
            this._saving = false;
            this._dialogVisible = false;
            this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Змінну додано' });
            this._reloadComponentData();
          },
          error: (err) => {
            this._saving = false;
            console.error('*** VariableSelectorComponent onDialogSave (add): Error: ' + err);
            this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося додати змінну' });
          }
        });
    } else {
      this._writeQuery = this.prodApi.putPriceVariableById(pv.VARID, pv)
        .subscribe({
          next: () => {
            this._saving = false;
            this._dialogVisible = false;
            this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Змінну оновлено' });
            this._reloadComponentData();
          },
          error: (err) => {
            this._saving = false;
            console.error('*** VariableSelectorComponent onDialogSave (edit): Error: ' + err);
            this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Не вдалося оновити змінну' });
          }
        });
    }
  }

  /** Cancels the Add/Edit dialog without saving. */
  onDialogCancel(): void {
    this._dialogVisible = false;
    this._dialogModel = null;
  }

}
